import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { showConfirmDialog } from '../../services/redux/app/app.actions';
import resourceSlug from '../../constant/resource-slug';
import { INVOICE_STATUS, PAYMENT_STATUS } from '../../constant/invoice';
import { hasPermission, isSystemUsers } from '../../services/util/auth';
import { NavigationContext } from '../../App';
import { UUID_REGEX } from '../../constant';
import { sleep } from '../../services/util';

export const useValidateInvoice = (option) => {
  const [isValidating, setIsValidating] = useState(false);
  const [invoiceId, setInvoiceId] = useState();
  const notify = useNotify();
  const t = useTranslate();
  const refresh = useRefresh();
  const dispatch = useDispatch();

  const confirm = (invoiceId) => {
    dispatch(
      showConfirmDialog({
        isOpen: true,
        title: 'resources.invoice.validateTitle',
        content: 'resources.invoice.validateMessage',
        onConfirm: () => validateInvoice(invoiceId),
      }),
    );
  };

  const validateInvoice = async (invoiceId) => {
    setIsValidating(true);
    setInvoiceId(invoiceId);

    try {
      await axios.post(`/api/${resourceSlug.INVOICE}/${invoiceId}/validate`);
      notify(t('resources.invoice.validatedSuccess'), 'success');

      if (option?.onSuccess) {
        option?.onSuccess?.();
      } else {
        refresh();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setInvoiceId();
      setIsValidating(false);
    }
  };

  return {
    validateInvoice: confirm,
    isValidating,
    invoiceId,
  };
};

export const useRegenerateInvoice = (option) => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [invoiceId, setInvoiceId] = useState();
  const notify = useNotify();
  const t = useTranslate();
  const refresh = useRefresh();
  const dispatch = useDispatch();

  const confirm = (invoiceId) => {
    dispatch(
      showConfirmDialog({
        isOpen: true,
        title: 'resources.invoice.regenerateTitle',
        content: 'resources.invoice.regenerateMessage',
        onConfirm: () => regenerateInvoice(invoiceId),
      }),
    );
  };

  const regenerateInvoice = async (invoiceId) => {
    setIsRegenerating(true);
    setInvoiceId(invoiceId);
    try {
      await axios.post(`/api/${resourceSlug.INVOICE}/${invoiceId}/regenerate`);
      // To ensure the data is up-to-date
      await sleep(5000);
      notify(t('resources.invoice.regeneratedSuccess'), 'success');

      if (option?.onSuccess) {
        option?.onSuccess?.();
      } else {
        refresh();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setInvoiceId();
      setIsRegenerating(false);
    }
  };

  return {
    regenerateInvoice: confirm,
    isRegenerating,
    invoiceId,
  };
};

export const useInvoiceIdsSync = () => {
  const location = useLocation();
  const history = useHistory();

  const getInvoiceIds = () => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get('invoiceIds');
    if (!raw) return [];
    return raw.split(',');
  };

  const setInvoiceIds = (ids) => {
    const searchParams = new URLSearchParams(location.search);

    if (ids.length > 0) {
      searchParams.set('invoiceIds', ids.join(','));
    } else {
      searchParams.delete('invoiceIds');
    }

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  return {
    invoiceIds: getInvoiceIds(),
    setInvoiceIds,
  };
};

export const useInvoiceStatus = () => {
  const isSU = isSystemUsers();
  const hasUpdatePermission = hasPermission(resourceSlug.INVOICE, 'update');
  const hasCreateInvoicePermission = hasPermission(resourceSlug.INVOICE, 'create');
  const hasExportPermission = hasPermission(resourceSlug.INVOICE, 'export');
  const hasCreatePaymentPermission = hasPermission(resourceSlug.PAYMENT, 'create');

  return {
    isPayableInvoice: (record) =>
      record?.invoiceStatus === INVOICE_STATUS.VALIDATED &&
      [PAYMENT_STATUS.UNPAID, PAYMENT_STATUS.FAILED].includes(record?.paymentStatus) &&
      hasCreatePaymentPermission,
    isValidatableInvoice: (record) => isSU && hasUpdatePermission && record?.invoiceStatus === INVOICE_STATUS.OPEN,
    isRegeneratableInvoice: (record) =>
      isSU &&
      hasCreateInvoicePermission &&
      [PAYMENT_STATUS.UNPAID, PAYMENT_STATUS.WAIVED].includes(record?.paymentStatus),
    isExportableInvoice: () => hasExportPermission,
  };
};

export const useInvoiceDetailsNavigation = () => {
  const { setNavigationData } = useContext(NavigationContext);
  const { pathname } = useLocation();

  useEffect(() => {
    setNavigationData({
      pageTitleInfo: {
        label: 'resources.invoice.name',
        showSubLabel: false,
      },
      breadcrumbs: [
        {
          label: 'ra.page.home',
          link: '/',
        },
        {
          label: 'resources.group.finance.name',
        },
        {
          label: 'resources.invoice.name',
          link: `/${resourceSlug.INVOICE}`,
        },
        {
          label: `#${pathname
            .split('/')
            .find((path) => path.match(UUID_REGEX))
            .slice(0, 4)}`,
          isActive: true,
        },
      ],
    });

    return () => {
      setNavigationData({});
    };
  }, [pathname]);
};

export const usePayInvoices = (option) => {
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  const generatePaymentQA = async (data = {}) => {
    setIsGeneratingQR(true);
    try {
      const { invoiceIds, paymentChannel, description, paymentProofKeys, paymentMethod } = data;
      const { data: paymentData } = await axios.post(`/api/${resourceSlug.PAYMENT}`, {
        invoiceIds,
        paymentChannel,
        description,
        paymentProofKeys,
        paymentMethod,
      });
      option?.onSuccess?.(paymentData.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  return {
    generatePaymentQA,
    isGeneratingQR,
  };
};

export const PaymentContext = createContext({});

export const usePayment = () => {
  const values = useContext(PaymentContext);

  if (values === null) {
    throw new Error('Make sure to use `PaymentContext` before using the steps.');
  }

  return values;
};
